import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Table, Input, message, Image, Typography } from 'antd';

const { Search } = Input;
const { Title } = Typography;

const columns = [
    {
        title: "#",
        dataIndex: "id",
        width: "60px",
        align: "center",
        render: (id) => <strong>{id}</strong>,
    },
    {
        title: "Title",
        dataIndex: "title",
        width: "250px",
        ellipsis: true,
    },
    {
        title: "Price",
        dataIndex: "price",
        render: (price) => `₹${parseFloat(price).toFixed(2)}`,
        width: "100px",
        align: "right",
        sorter: (a, b) => a.price - b.price,
    },
    {
        title: "Description",
        dataIndex: "description",
        ellipsis: true,
    },
    {
        title: "Category",
        dataIndex: "category",
        width: "150px",
        align: "center",
    },
    {
        title: "Sold",
        dataIndex: "sold",
        render: (sold) => (sold ? "Yes" : "No"),
        width: "80px",
        align: "center",
    },
    {
        title: "Date",
        dataIndex: "dateOfSale",
        render: (date) => moment(date).format("DD MMM YYYY"),
        width: "140px",
        align: "center",
    },
    {
        title: "Image",
        dataIndex: "image",
        render: (url) => (
            <Image src={url} alt="Product" width={60} style={{ borderRadius: '8px' }} preview={{ mask: <span>View</span> }} />
        ),
        width: "100px",
    },
];

const Transactions = ({ month, monthText }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [search, setSearch] = useState('');
    const [totalItems, setTotalItems] = useState(0);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('https://fakestoreapi.com/products');
            let products = await response.json();

            // Add missing fields to match your data structure
            products = products.map(product => ({
                ...product,
                sold: Math.random() < 0.5, // Random boolean for sold status
                dateOfSale: moment().subtract(Math.floor(Math.random() * 12), 'months').format() // Random date within last 12 months
            }));

            // Filter data based on selected month and search term
            const filteredData = products.filter(item => {
                const itemDate = moment(item.dateOfSale);
                const matchesMonth = itemDate.month() === month - 1;
                const searchLower = search.toLowerCase();
                const matchesSearch = search === '' || 
                    item.title.toLowerCase().includes(searchLower) ||
                    item.description.toLowerCase().includes(searchLower) ||
                    item.price.toString().includes(searchLower);
                
                return matchesMonth && matchesSearch;
            });

            // Calculate pagination
            const { current, pageSize } = pagination;
            const startIndex = (current - 1) * pageSize;
            const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);
            
            setData(paginatedData);
            setTotalItems(filteredData.length);
            
            if (filteredData.length === 0) {
                message.info(`No transactions found for ${monthText}`);
            } else {
                message.success('Data loaded successfully');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Error loading data. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [month, monthText, pagination.current, pagination.pageSize, search]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div style={{ padding: '30px', backgroundColor: '#f5f5f5' }}>
            <div style={{ backgroundColor: '#ffffff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', marginBottom: '20px' }}>
                <Title level={3} style={{ marginBottom: '10px', color: '#333' }}>
                    Transactions for {monthText}
                </Title>
                <Search
                    placeholder="Search transactions"
                    allowClear
                    onSearch={(value) => {
                        setSearch(value);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    style={{ width: '320px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                />
            </div>
            <Table
                columns={columns}
                rowKey="id"
                dataSource={data}
                pagination={{
                    ...pagination,
                    total: totalItems,
                    onChange: (page, pageSize) => setPagination({ current: page, pageSize })
                }}
                loading={loading}
                size="middle"
                bordered
                scroll={{ x: '100%', y: 540 }}
                style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}
            />
        </div>
    );
}

export default Transactions;